import {
  Component,
  Input,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { ModalController } from '@ionic/angular';
import { DataServerService } from 'src/app/services/api/data-server.service';
import { AlertService } from 'src/app/services/base/alert.service';
import { OfflineService } from 'src/app/services/manager/offline.service';
import { SubscriberService } from 'src/app/services/manager/subscriber.service';
import { Order } from 'src/app/interfaces/orders';

interface OrderVersion {
  id: string;
  versionId: string;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
  snapshot: any;
  color: 'blue' | 'yellow';
  colorLabel: string;
  selected: boolean;
}

@Component({
  selector: 'app-order-history',
  templateUrl: './order-history.component.html',
  styleUrls: ['./order-history.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderHistoryComponent implements OnInit {
  @Input() orderId!: string;
  @Input() orderNumber?: string;

  loading = true;
  versions: OrderVersion[] = [];
  totalVersions = 0;

  constructor(
    private modalController: ModalController,
    private dataServer: DataServerService,
    private alert: AlertService,
    private offline: OfflineService,
    private sub: SubscriberService,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit() {
    await this.loadVersions();
  }

  async loadVersions() {
    this.loading = true;
    this.cdr.markForCheck();

    const entries: OrderVersion[] = [];

    // Serverseitige Versionen (blau, mit Autor) — bereits bestätigt vom Server
    try {
      const res: any = await this.dataServer.getOrderVersions(this.orderId);
      if (res?.status === 200 && res?.data?.docs) {
        // Collect all user IDs from order snapshots
        const userIds = new Set<string>();
        res.data.docs.forEach((version: any) => {
          const updatedBy = version.version?.updatedBy;
          if (typeof updatedBy === 'string') {
            userIds.add(updatedBy);
          }
        });

        // Load user data if we have user IDs
        const userMap = new Map<string, any>();
        if (userIds.size > 0) {
          try {
            const usersRes: any = await this.dataServer.getUsers();
            if (usersRes?.status === 200 && usersRes?.data?.docs) {
              usersRes.data.docs.forEach((user: any) => {
                userMap.set(user.id, user);
              });
            }
          } catch (e) {
            console.error('Failed to load users:', e);
          }
        }

        entries.push(...res.data.docs.map((version: any) => {
          let userName = 'System';
          const updatedBy = version.version?.updatedBy;

          if (updatedBy) {
            if (typeof updatedBy === 'object') {
              userName = updatedBy.username || updatedBy.email || 'Unbekannt';
            } else if (typeof updatedBy === 'string') {
              const user = userMap.get(updatedBy);
              userName = user ? (user.username || user.email || updatedBy) : updatedBy;
            }
          }

          return {
            id: version.id,
            versionId: version.id,
            createdAt: version.createdAt,
            updatedAt: version.updatedAt,
            updatedBy: userName,
            snapshot: version.version,
            color: 'blue' as const,
            colorLabel: 'Gesendet',
            selected: false,
          };
        }));

        this.totalVersions = res.data.totalDocs || 0;
      }
    } catch (e) {
      console.error('loadVersions error:', e);
      // Server-Versionen nicht verfügbar (z. B. offline) — lokale Quellen unten reichen als Fallback
    }

    // Lokal unsynchronisiert (gelb) — noch nicht auf dem Server angekommen
    const offlineOrder = this.sub.offlineOrders.getValue().find(o => o.orderId === this.orderId);
    if (offlineOrder) {
      entries.push(this.buildLocalEntry(offlineOrder, 'yellow', 'Ungesendet', offlineOrder.updated));
    }

    // Lokal synchronisiert, noch nicht endgültig entfernt (blau) — gleiche Kategorie wie Server-Versionen
    const syncedOrder = this.sub.syncedOfflineOrders.getValue().find(o => o.orderId === this.orderId);
    if (syncedOrder) {
      entries.push(this.buildLocalEntry(syncedOrder, 'blue', 'Gesendet (lokal bestätigt)', syncedOrder.updated));
    }

    // Lokaler Revisions-Buffer (gelb) — bis zu 10 Zwischenstände
    try {
      const revisions = await this.offline.getOrderRevisions(this.orderId);
      for (const rev of revisions) {
        entries.push(this.buildLocalEntry(rev.order, 'yellow', 'Lokaler Zwischenstand', new Date(rev.timestamp).toISOString()));
      }
    } catch (e) {
      console.error('Failed to load order revisions:', e);
    }

    entries.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    this.versions = entries;

    this.loading = false;
    this.cdr.markForCheck();
  }

  private buildLocalEntry(order: Order, color: 'blue' | 'yellow', colorLabel: string, updatedAt: string): OrderVersion {
    return {
      id: `local-${order.orderId}-${updatedAt}`,
      versionId: `local-${order.orderId}-${updatedAt}`,
      createdAt: order.created ?? updatedAt,
      updatedAt,
      updatedBy: 'Lokal (dieses Gerät)',
      snapshot: order,
      color,
      colorLabel,
      selected: false,
    };
  }

  get selectedVersions(): OrderVersion[] {
    return this.versions.filter(v => v.selected);
  }

  get allSelected(): boolean {
    return this.versions.length > 0 && this.versions.every(v => v.selected);
  }

  toggleSelectAll() {
    const next = !this.allSelected;
    this.versions.forEach(v => (v.selected = next));
    this.cdr.markForCheck();
  }

  downloadVersion(version: OrderVersion) {
    this.downloadAsJson([version.snapshot], `order-${this.orderId}-${version.versionId}`);
  }

  downloadSelected() {
    const selected = this.selectedVersions;
    if (selected.length === 0) return;
    this.downloadAsJson(selected.map(v => v.snapshot), `order-${this.orderId}-selected`);
  }

  private downloadAsJson(orders: unknown[], filePrefix: string) {
    const blob = new Blob([JSON.stringify(orders, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filePrefix}-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async restoreAsNewOrder(version: OrderVersion) {
    const confirmed = await this.alert.presentConfirmationAlert(
      'Als neuen Auftrag wiederherstellen',
      'Es wird ein komplett neuer Auftrag mit diesem Stand angelegt (neue Auftragsnummer). Der ursprüngliche Auftrag bleibt unverändert. Fortfahren?'
    );
    if (!confirmed) return;

    const restored = await this.offline.restoreVersionAsNewOrder(version.snapshot as Order);
    this.alert.presentToast(`Neuer Auftrag "${restored.orderTitle}" wurde angelegt.`);
  }

  trackByVersionId(index: number, item: OrderVersion): string {
    return item.versionId || index.toString();
  }

  close() {
    this.modalController.dismiss();
  }
}
